// Standard MIDI File export of the chords (F8.2): block chords, a bass line on the roots, and an
// optional click. Written by hand (format 1, a few dozen lines) rather than with a library.
import type { AnalysisResult } from '../analysis/types';
import { beatInBar } from '../analysis/grid';
import { chordVoicing } from '../practice/events';

export const PPQ = 480;

export interface MidiOptions {
  /** Semitones to shift the chords, to match the transposed chart. */
  transpose: number;
  bass: boolean;
  click: boolean;
}

interface NoteEvent {
  tick: number;
  /** Status byte without the channel. */
  kind: 0x90 | 0x80;
  channel: number;
  note: number;
  velocity: number;
}

function varLen(value: number): number[] {
  const bytes = [value & 0x7f];
  let v = value >> 7;
  while (v > 0) {
    bytes.unshift((v & 0x7f) | 0x80);
    v >>= 7;
  }
  return bytes;
}

const u32 = (n: number) => [(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff];
const u16 = (n: number) => [(n >>> 8) & 0xff, n & 0xff];
const ascii = (s: string) => Array.from(s, (c) => c.charCodeAt(0));

function track(events: number[]): number[] {
  return [...ascii('MTrk'), ...u32(events.length), ...events];
}

function noteTrack(name: string, program: number, channel: number, notes: NoteEvent[]): number[] {
  // Note-offs before note-ons at the same tick, so repeated notes retrigger cleanly.
  const sorted = [...notes].sort((a, b) => a.tick - b.tick || a.kind - b.kind);
  const bytes = [0, 0xff, 0x03, ...varLen(name.length), ...ascii(name)];
  if (channel !== 9) bytes.push(0, 0xc0 | channel, program);
  let last = 0;
  for (const e of sorted) {
    bytes.push(...varLen(e.tick - last), e.kind | e.channel, e.note, e.velocity);
    last = e.tick;
  }
  bytes.push(0, 0xff, 0x2f, 0);
  return track(bytes);
}

/** The chords as a .mid file (format 1: tempo track plus chord, bass and click tracks). */
export function chordsToMidi(result: AnalysisResult, o: MidiOptions): Uint8Array {
  const grid = result.beatGrid;
  const bpm = grid.bpm > 0 ? grid.bpm : 120;
  const first = grid.firstDownbeat;
  // The chart starts at the first downbeat (a pickup bar is shifted to begin the file).
  const tickOf = (beat: number) =>
    Math.max(0, beat - first + (first > 0 ? grid.beatsPerBar : 0)) * PPQ;

  const chords: NoteEvent[] = [];
  const bass: NoteEvent[] = [];
  for (const seg of result.segments) {
    if (!seg.chord) continue;
    const [root, ...upper] = chordVoicing(seg.chord, o.transpose);
    const on = tickOf(seg.startBeat);
    const off = tickOf(seg.startBeat + seg.beatCount) - 10;
    for (const note of upper) {
      chords.push({ tick: on, kind: 0x90, channel: 0, note, velocity: 72 });
      chords.push({ tick: off, kind: 0x80, channel: 0, note, velocity: 0 });
    }
    if (root !== undefined) {
      bass.push({ tick: on, kind: 0x90, channel: 1, note: root - 12, velocity: 90 });
      bass.push({ tick: off, kind: 0x80, channel: 1, note: root - 12, velocity: 0 });
    }
  }
  const click: NoteEvent[] = [];
  if (o.click) {
    for (let b = 0; b < grid.beats.length; b++) {
      const accent = beatInBar(grid, b) === 1;
      const note = accent ? 76 : 77; // high / low wood block
      click.push({ tick: tickOf(b), kind: 0x90, channel: 9, note, velocity: accent ? 110 : 80 });
      click.push({ tick: tickOf(b) + PPQ / 4, kind: 0x80, channel: 9, note, velocity: 0 });
    }
  }

  const tempo = Math.round(60_000_000 / bpm);
  const setTempo = [0, 0xff, 0x51, 0x03, (tempo >> 16) & 0xff, (tempo >> 8) & 0xff, tempo & 0xff];
  // Time signature: beats per bar over a quarter note (2 = 2^2), standard clocks.
  const timeSignature = [0, 0xff, 0x58, 0x04, grid.beatsPerBar, 2, 24, 8];
  const endOfTrack = [0, 0xff, 0x2f, 0];
  const tempoTrack = track([...setTempo, ...timeSignature, ...endOfTrack]);
  const tracks = [
    tempoTrack,
    noteTrack('Chords', 0, 0, chords),
    ...(o.bass ? [noteTrack('Bass', 33, 1, bass)] : []),
    ...(o.click ? [noteTrack('Click', 0, 9, click)] : []),
  ];
  const header = [...ascii('MThd'), ...u32(6), ...u16(1), ...u16(tracks.length), ...u16(PPQ)];
  return Uint8Array.from([...header, ...tracks.flat()]);
}
